"use client";
import { useState, useEffect, useCallback } from "react";
import { AppSidebar } from "@/components/app-sidebar";
import { useGlobal } from "./contexts/globalcontext";
import { localUrl } from "@/api/api";

function extractPermissionsFromMerchant(
  merchantData: Record<string, unknown> | null,
): string[] | null {
  if (!merchantData) return null;
  if (Array.isArray(merchantData.permission_names)) {
    return merchantData.permission_names.filter(
      (n): n is string => typeof n === "string",
    );
  }
  if (Array.isArray(merchantData.permissions)) {
    return merchantData.permissions
      .map((p: unknown) =>
        typeof p === "string"
          ? p
          : (p as { name?: string })?.name,
      )
      .filter((n): n is string => typeof n === "string");
  }
  const roles = Array.isArray(merchantData.roles)
    ? (merchantData.roles as { permissions?: { name?: string }[] }[])
    : [];
  if (roles.length > 0 && Array.isArray(roles[0]?.permissions)) {
    return roles[0].permissions
      .map((p) => p?.name)
      .filter((n): n is string => typeof n === "string");
  }
  return null;
}

function extractRoleNameFromMerchant(
  merchantData: Record<string, unknown> | null,
): string | null {
  if (!merchantData) return null;
  if (typeof merchantData.role_name === "string" && merchantData.role_name) {
    return merchantData.role_name;
  }
  const roles = Array.isArray(merchantData.roles)
    ? (merchantData.roles as { name?: string }[])
    : [];
  if (roles.length > 0 && typeof roles[0]?.name === "string") {
    return roles[0].name;
  }
  return null;
}

export default function SidebarWithPlan(
  props: Omit<
    React.ComponentProps<typeof AppSidebar>,
    | "planFeatures"
    | "addonTypeId"
    | "showInteractiveAds"
    | "isMainBranch"
    | "userPermissions"
    | "userRoleName"
  >,
) {
  const {
    token,
    merchantId,
    districtMerchantId,
    showBranch,
    userData,
    merchantData,
  } = useGlobal();
  const [addonTypeId, setAddonTypeId] = useState<number | null>(null);
  const [showInteractiveAds, setShowInteractiveAds] = useState(false);
  const [userPermissions, setUserPermissions] = useState<string[] | null>(() =>
    extractPermissionsFromMerchant(merchantData),
  );
  const [userRoleName, setUserRoleName] = useState<string | null>(() =>
    extractRoleNameFromMerchant(merchantData),
  );
  const [isMainBranch, setIsMainBranch] = useState<boolean>(() => {
    const branchObj = showBranch as {
      data?: { is_main?: number | boolean };
      is_main?: number | boolean;
    } | null;
    return (
      branchObj?.data?.is_main === 1 ||
      branchObj?.data?.is_main === true ||
      branchObj?.is_main === 1 ||
      branchObj?.is_main === true
    );
  });

  const [loading, setLoading] = useState(true);

  // Sync permissions/role if merchantData in global context updates
  useEffect(() => {
    const perms = extractPermissionsFromMerchant(merchantData);
    if (perms) setUserPermissions(perms);
    const rName = extractRoleNameFromMerchant(merchantData);
    if (rName) setUserRoleName(rName);
  }, [merchantData]);

  // Function to fetch addon, branch, and live user permission data
  const fetchCheckLimitCount = useCallback(async () => {
    if (!merchantId || !token) {
      setLoading(false);
      return;
    }

    try {
      // Fetch live permissions for the logged-in user via /api/merchant/getUsers/{merchantId}
      const currentUserId = Number(userData?.id || merchantData?.user_id);
      if (currentUserId) {
        try {
          const usersRes = await fetch(
            `${localUrl}/api/merchant/getUsers/${merchantId}`,
            {
              method: "GET",
              headers: {
                Accept: "application/json",
                Authorization: `Bearer ${token}`,
              },
            },
          );
          if (usersRes.ok) {
            const usersData = await usersRes.json();
            const userList = Array.isArray(usersData)
              ? usersData
              : Array.isArray(usersData?.merchantUser)
                ? usersData.merchantUser
                : Array.isArray(usersData?.data)
                  ? usersData.data
                  : [];
            const currentUserObj = userList.find(
              (u: { id?: number }) => Number(u?.id) === currentUserId,
            );
            const livePerms = currentUserObj?.merchant?.permissions;
            if (Array.isArray(livePerms)) {
              const names = livePerms
                .map((p: { name?: string }) => p?.name)
                .filter((n: unknown): n is string => typeof n === "string");
              setUserPermissions(names);
            }
            const liveRole = currentUserObj?.merchant?.roles?.[0]?.name;
            if (typeof liveRole === "string" && liveRole) {
              setUserRoleName(liveRole);
            }
          }
        } catch (permErr) {
          console.error("Error fetching user permissions for sidebar:", permErr);
        }
      }

      // Check branch is_main via /api/branch/show/{districtMerchantId}
      if (districtMerchantId) {
        try {
          const branchResponse = await fetch(
            `${localUrl}/api/branch/show/${districtMerchantId}`,
            {
              method: "GET",
              headers: {
                Accept: "application/json",
                Authorization: `Bearer ${token}`,
              },
            },
          );
          if (branchResponse.ok) {
            const branchData = await branchResponse.json();
            const isMain =
              branchData?.data?.is_main === 1 ||
              branchData?.data?.is_main === true ||
              branchData?.is_main === 1 ||
              branchData?.is_main === true;
            setIsMainBranch(isMain);
          } else {
            setIsMainBranch(false);
          }
        } catch (branchError) {
          console.error("Error fetching branch show:", branchError);
          setIsMainBranch(false);
        }
      } else {
        setIsMainBranch(false);
      }

      // Check for addon_type_id 5 (Stamp)
      const response5 = await fetch(
        `${localUrl}/api/merchant/checkLimitCount/${merchantId}?addon_type_id=5`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      // Check for addon_type_id 6 (Points)
      const response6 = await fetch(
        `${localUrl}/api/merchant/checkLimitCount/${merchantId}?addon_type_id=6`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      // Check for addon_type 17 (Interactive Ads) via getMerchantAddOns
      const response17 = await fetch(
        `${localUrl}/api/merchant/getMerchantAddOns/${merchantId}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data17 = await response17.json();

      // Check if addon_type_id 17 exists in purchased addons
      const addons17 = Array.isArray(data17)
        ? data17
        : data17?.data ?? [];
      const hasAddon17 =
        response17.ok &&
        Array.isArray(addons17) &&
        addons17.some(
          (item: { addon_type_id?: number }) => item.addon_type_id === 17,
        );

      setShowInteractiveAds(hasAddon17);

      let hasAddonType5 = false;
      let hasAddonType6 = false;

      if (response5.ok) {
        const data5 = await response5.json();
        hasAddonType5 =
          data5 &&
          ((Array.isArray(data5) && data5.length > 0) ||
            (data5.data &&
              Array.isArray(data5.data) &&
              data5.data.length > 0) ||
            data5.count > 0 ||
            data5.limit_count > 0);
      }

      if (response6.ok) {
        const data6 = await response6.json();
        hasAddonType6 =
          data6 &&
          ((Array.isArray(data6) && data6.length > 0) ||
            (data6.data &&
              Array.isArray(data6.data) &&
              data6.data.length > 0) ||
            data6.count > 0 ||
            data6.limit_count > 0);
      }

      const showStampPoints = hasAddonType5 || hasAddonType6;
      setAddonTypeId(showStampPoints ? (hasAddonType5 ? 5 : 6) : null);
    } catch (error) {
      console.error("Error fetching checkLimitCount:", error);
      setAddonTypeId(null);
      setShowInteractiveAds(false);
    } finally {
      setLoading(false);
    }
  }, [merchantId, districtMerchantId, token, userData?.id, merchantData?.user_id]);

  // Initial fetch on mount
  useEffect(() => {
    fetchCheckLimitCount();
  }, [fetchCheckLimitCount]);

  // Check for localStorage flag to trigger re-fetch when addon is purchased
  useEffect(() => {
    const checkAddonUpdate = () => {
      const addonUpdated = localStorage.getItem("addon_updated");
      if (addonUpdated === "true") {
        localStorage.removeItem("addon_updated");
        setLoading(true);
        fetchCheckLimitCount();
      }
    };

    checkAddonUpdate();
    const interval = setInterval(checkAddonUpdate, 30000);
    return () => clearInterval(interval);
  }, [fetchCheckLimitCount]);

  if (loading) {
    return (
      <AppSidebar
        {...props}
        addonTypeId={null}
        showInteractiveAds={false}
        isMainBranch={isMainBranch}
        userPermissions={userPermissions}
        userRoleName={userRoleName}
        style={{ fontSize: "10px" }}
      />
    );
  }

  return (
    <AppSidebar
      {...props}
      addonTypeId={addonTypeId}
      showInteractiveAds={showInteractiveAds}
      isMainBranch={isMainBranch}
      userPermissions={userPermissions}
      userRoleName={userRoleName}
      style={{ fontSize: "10px" }}
    />
  );
}
